import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Clock, Calendar as CalendarIcon, Globe } from 'lucide-react';
import axios from 'axios';
import CalendarPicker from './CalendarPicker';
import TimeSlots from './TimeSlots';
import AttendeeDetails from './AttendeeDetails';

const CALENDAR_TITLE = 'RentDirect Onboarding Call';
const DURATION_LABEL = '1 hr 30 min';

// A curated set of common timezones for the dropdown, plus the visitor's own.
const COMMON_TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Dubai', 'Asia/Kolkata',
  'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney', 'UTC',
];

const getLocalTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
};

// Offset label like "GMT+5:30" for a given IANA tz.
const tzOffsetLabel = (tz) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(new Date());
    const name = parts.find((p) => p.type === 'timeZoneName');
    return name ? name.value : '';
  } catch {
    return '';
  }
};

const formatLongDate = (dateKey, tz) => {
  // dateKey is 'YYYY-MM-DD' in the selected tz; render as "Wed, Jun 17, 2026".
  const [y, m, d] = dateKey.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  }).format(dt);
};

const formatSlotTime = (iso, tz) =>
  new Intl.DateTimeFormat('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz,
  }).format(new Date(iso));

const ScheduleStep = ({ appointment, onSelectSlot, attendee, onAttendeeChange, attendeeErrors }) => {
  const [timezone, setTimezone] = useState(getLocalTz());
  const [slotsByDate, setSlotsByDate] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [viewMonth, setViewMonth] = useState(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() };
  });
  const [selectedDate, setSelectedDate] = useState(null); // 'YYYY-MM-DD'

  const tzOptions = useMemo(() => {
    const local = getLocalTz();
    const set = [local, ...COMMON_TIMEZONES.filter((t) => t !== local)];
    return Array.from(new Set(set));
  }, []);

  // Fetch availability for the currently-viewed month only. GHL's free-slots
  // endpoint caps the range at 31 days, so we query month-by-month (start of the
  // month, or now if it's the current month → end of the month) and merge
  // results so navigating between months keeps prior data.
  const fetchSlots = useCallback(async (tz, { year, month }) => {
    setLoading(true);
    setLoadError(null);
    try {
      const now = Date.now();
      const monthStart = new Date(year, month, 1).getTime();
      const monthEnd = new Date(year, month + 1, 0, 23, 59, 59).getTime();
      const startDate = Math.max(monthStart, now);
      const endDate = monthEnd;
      if (endDate <= startDate) {
        // Entire month is in the past — nothing to fetch.
        setLoading(false);
        return;
      }
      const resp = await axios.get('/api/ghl-slots', {
        params: { startDate, endDate, timezone: tz },
      });
      setSlotsByDate((prev) => ({ ...prev, ...(resp.data.slots || {}) }));
    } catch (err) {
      setLoadError(
        (err.response && err.response.data && err.response.data.error) ||
        'Could not load available times. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Refetch when the timezone changes (reset accumulated slots) ...
  useEffect(() => {
    setSlotsByDate({});
    fetchSlots(timezone, viewMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone, fetchSlots]);

  // ... and when the user navigates to a different month (merge in new data).
  useEffect(() => {
    fetchSlots(timezone, viewMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMonth.year, viewMonth.month]);

  // When timezone changes, the previously selected slot no longer applies.
  const handleTzChange = (tz) => {
    setTimezone(tz);
    setSelectedDate(null);
    onSelectSlot(null);
  };

  const handleSelectDate = (dateKey) => {
    setSelectedDate(dateKey);
    onSelectSlot(null); // clear chosen time until they pick one for this date
  };

  const handleSelectSlot = (iso) => {
    onSelectSlot({ startTime: iso, timezone });
  };

  const availableDates = useMemo(() => new Set(Object.keys(slotsByDate)), [slotsByDate]);
  const daySlots = selectedDate ? slotsByDate[selectedDate] || [] : [];

  // Info-box: show selected slot time range if chosen, else selected date, else prompt.
  const infoDateLine = (() => {
    if (appointment && appointment.startTime) {
      const start = appointment.startTime;
      const startLabel = formatSlotTime(start, timezone);
      // end = start + 90 min
      const end = new Date(new Date(start).getTime() + 90 * 60 * 1000).toISOString();
      const endLabel = formatSlotTime(end, timezone);
      const dayKey = selectedDate || Object.keys(slotsByDate).find((k) => (slotsByDate[k] || []).includes(start));
      return `${startLabel} - ${endLabel}, ${dayKey ? formatLongDate(dayKey, timezone) : ''}`;
    }
    if (selectedDate) return formatLongDate(selectedDate, timezone);
    return 'Select a date';
  })();

  return (
    <div className="preob-cal">
      <h2 className="preob-cal__title">{CALENDAR_TITLE}</h2>

      <div className="preob-cal__info">
        <div className="preob-cal__info-row">
          <Clock size={16} />
          <span>{DURATION_LABEL}</span>
        </div>
        <div className="preob-cal__info-row">
          <CalendarIcon size={16} />
          <span>{infoDateLine}</span>
        </div>
        <div className="preob-cal__info-row">
          <Globe size={16} />
          <span>{timezone.replace('_', ' ')} ({tzOffsetLabel(timezone)})</span>
        </div>
      </div>

      {loadError && <p className="preob-form__error preob-cal__error">{loadError}</p>}

      <div className="preob-cal__body">
        <CalendarPicker
          viewMonth={viewMonth}
          setViewMonth={setViewMonth}
          availableDates={availableDates}
          selectedDate={selectedDate}
          onSelectDate={handleSelectDate}
          loading={loading}
        />

        <div className="preob-cal__tz">
          <label className="preob-cal__tz-label" htmlFor="preob-tz">Time zone</label>
          <div className="preob-cal__tz-select-wrap">
            <Globe size={16} className="preob-cal__tz-icon" />
            <select
              id="preob-tz"
              className="preob-input preob-input--select preob-cal__tz-select"
              value={timezone}
              onChange={(e) => handleTzChange(e.target.value)}
            >
              {tzOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tzOffsetLabel(tz)} {tz.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selectedDate && (
        <TimeSlots
          slots={daySlots}
          selected={appointment && appointment.startTime}
          formatTime={(iso) => formatSlotTime(iso, timezone)}
          onSelect={handleSelectSlot}
        />
      )}

      {appointment && appointment.startTime && (
        <AttendeeDetails
          value={attendee}
          onChange={onAttendeeChange}
          errors={attendeeErrors}
        />
      )}
    </div>
  );
};

export default ScheduleStep;
